/**
 * v3.4.7 Phase 1 — <PatternsTabV2>
 * ============================================================================
 * Refreshed Patterns tab. Consumes /api/patterns/insights and renders:
 *
 *   1. Data-completeness hint (empty-state when history is thin)
 *   2. Short-term (MoM) pattern list — icon + sparkline + narrative
 *   3. Goals lens — either impact of current patterns on goals, OR an
 *      empty-state nudge to create a goal
 *   4. Long-term (YoY) locked preview — placeholder until Phase 2 layers
 *      LLM narrative + real YoY data when user has 13+ months of history
 *
 * Phase 2 (next iteration) will layer LLM big-picture narrative from the
 * Emergent Universal Key at the top of section 1, and activate the YoY
 * comparisons in section 4.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sparkles, TrendingUp, TrendingDown, Minus, Lock, Target, Upload, Info, RefreshCw, Star, ArrowRight, Zap, Equal } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { formatMoney } from '@/lib/format';
import { CATEGORY_COLORS } from '@/lib/categorize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Tiny inline SVG sparkline. Renders a line across [x0..x_n] with the
    value range mapped to the box height. No axes, no labels — the trend
    shape is the whole point. */
function Sparkline({ values, color = 'hsl(var(--gold))', width = 84, height = 28 }) {
  if (!Array.isArray(values) || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const dx = width / (values.length - 1);
  const points = values.map((v, i) => {
    const x = i * dx;
    const y = height - ((v - min) / range) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} className="shrink-0" aria-hidden="true">
      <polyline
        fill="none" stroke={color} strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
}

function DirectionIcon({ direction }) {
  if (direction === 'up') return <TrendingUp className="h-4 w-4 text-rose-500" />;
  if (direction === 'down') return <TrendingDown className="h-4 w-4 text-emerald-500" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

/** v3.5.0 — Small star rating built from Lucide `Star`. Filled for the
    confidence tier, hollow for the rest. Never more than 5. */
function ConfidenceStars({ confidence }) {
  if (!confidence) return null;
  const filled = Math.max(1, Math.min(5, confidence.stars || 3));
  return (
    <div
      className="inline-flex items-center gap-1"
      title={`${confidence.label} confidence · ${confidence.hint}`}
      data-testid={`pattern-confidence-${confidence.tier}`}
    >
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
        {confidence.label}
      </span>
      <span className="flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`h-2.5 w-2.5 ${i < filled ? 'text-[hsl(var(--gold))] fill-[hsl(var(--gold))]' : 'text-muted-foreground/30'}`}
          />
        ))}
      </span>
    </div>
  );
}

/** v3.5.0 — Featured card. The single biggest change gets its own
    large, prominent surface. Uses gold accent border to signal "this is
    the one thing worth reading". Renders a large title, the narrative,
    the star confidence, and the recommendation footer. */
function FeaturedPatternCard({ pattern, baseCurrency, onReview }) {
  if (!pattern) return null;
  const color = CATEGORY_COLORS[pattern.category] || 'hsl(var(--gold))';
  return (
    <section
      className="rounded-2xl border border-[hsl(var(--gold))]/40 bg-gradient-to-br from-[hsl(var(--gold))]/[0.09] to-transparent p-5 space-y-4"
      data-testid="pattern-featured-card"
    >
      <div className="flex items-center gap-2">
        <Zap className="h-4 w-4 text-[hsl(var(--gold))]" />
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
          Biggest change this month
        </p>
      </div>
      <div className="flex items-start gap-4">
        <div
          className="h-14 w-14 shrink-0 rounded-2xl grid place-items-center"
          style={{ backgroundColor: `${color}22` }}
        >
          <DirectionIcon direction={pattern.direction} />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <h3 className="font-display text-xl font-semibold tracking-tight leading-tight">
            <span style={{ color }}>{pattern.category}</span> {pattern.direction === 'up' ? 'rose' : pattern.direction === 'down' ? 'eased' : 'held steady'} {Math.abs(pattern.mom_delta_pct)}% this month
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {pattern.narrative}
          </p>
          <Sparkline values={pattern.sparkline} color={color} width={220} height={40} />
        </div>
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-[hsl(var(--gold))]/20">
        <ConfidenceStars confidence={pattern.confidence} />
        <button
          type="button"
          onClick={() => onReview?.(pattern)}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:opacity-70 transition-opacity"
          data-testid="pattern-featured-review"
        >
          Review this <ArrowRight className="h-3 w-3" />
        </button>
      </div>
      {pattern.recommendation && (
        <div className="rounded-xl bg-background/60 border border-border/60 px-3.5 py-2.5 flex items-start gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 shrink-0 mt-0.5">Next</span>
          <p className="text-sm leading-relaxed">{pattern.recommendation}</p>
        </div>
      )}
    </section>
  );
}

/** v3.5.0 — Quick-summary rail. Four compact tiles at the top of the
    Patterns tab. Renders whichever of the four slots have data. */
function QuickSummaryRail({ quick }) {
  if (!quick) return null;
  const tiles = [
    { key: 'up', label: 'Largest increase', icon: TrendingUp, tone: 'rose', data: quick.largest_increase, valueFmt: (d) => d.category, subFmt: (d) => `+${Math.abs(d.delta_pct).toFixed(0)}% vs last month` },
    { key: 'down', label: 'Largest decrease', icon: TrendingDown, tone: 'emerald', data: quick.largest_decrease, valueFmt: (d) => d.category, subFmt: (d) => `−${Math.abs(d.delta_pct).toFixed(0)}% vs last month` },
    { key: 'const', label: 'Most consistent', icon: Equal, tone: 'sky', data: quick.most_consistent, valueFmt: (d) => d.category, subFmt: (d) => `${d.cv_pct.toFixed(0)}% variance` },
    { key: 'grow', label: 'Fastest growing', icon: Zap, tone: 'amber', data: quick.fastest_growing, valueFmt: (d) => d.category, subFmt: (d) => `+${d.growth_pct.toFixed(0)}% vs 3 mo ago` },
  ];
  const visible = tiles.filter((t) => t.data);
  if (visible.length === 0) return null;
  const toneColors = {
    rose: 'text-rose-500 bg-rose-50 dark:bg-rose-950/30',
    emerald: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/30',
    sky: 'text-sky-500 bg-sky-50 dark:bg-sky-950/30',
    amber: 'text-amber-500 bg-amber-50 dark:bg-amber-950/30',
  };
  return (
    <section data-testid="pattern-quick-summary" className="space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
        Insights this month
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        {visible.map((t) => {
          const Icon = t.icon;
          return (
            <div
              key={t.key}
              className="rounded-2xl border border-border/70 bg-card p-3.5"
              data-testid={`pattern-quick-tile-${t.key}`}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <span className={`inline-flex items-center justify-center h-6 w-6 rounded-lg ${toneColors[t.tone]}`}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/70 truncate">
                  {t.label}
                </span>
              </div>
              <p className="text-sm font-semibold truncate" title={t.valueFmt(t.data)}>{t.valueFmt(t.data)}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{t.subFmt(t.data)}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PatternRow({ pattern, baseCurrency, onReview }) {
  const color = CATEGORY_COLORS[pattern.category] || 'hsl(var(--muted-foreground))';
  return (
    <div
      className="rounded-2xl border border-border/70 bg-card p-4 hover:shadow-sm transition-shadow"
      data-testid={`pattern-row-${pattern.id}`}
    >
      <div className="flex items-start gap-4">
        <div
          className="h-11 w-11 shrink-0 rounded-xl grid place-items-center"
          style={{ backgroundColor: `${color}22` }}
        >
          <DirectionIcon direction={pattern.direction} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold" style={{ color }}>{pattern.category}</span>
            <span className={`text-xs font-semibold tabular-nums ${pattern.direction === 'up' ? 'text-rose-500' : pattern.direction === 'down' ? 'text-emerald-500' : 'text-muted-foreground'}`}>
              {pattern.direction === 'up' ? '+' : pattern.direction === 'down' ? '' : ''}{pattern.mom_delta_pct}%
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{pattern.narrative}</p>
        </div>
        <Sparkline values={pattern.sparkline} color={color} />
      </div>
      {pattern.recommendation && (
        <div className="mt-3 pt-3 border-t border-border/60 flex items-start gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 shrink-0 mt-0.5">Next</span>
          <p className="text-sm leading-relaxed flex-1">{pattern.recommendation}</p>
        </div>
      )}
      <div className="mt-2 flex items-center justify-between">
        <ConfidenceStars confidence={pattern.confidence} />
        <button
          type="button"
          onClick={() => onReview?.(pattern)}
          className="text-xs font-medium text-primary hover:opacity-70 transition-opacity inline-flex items-center gap-1"
          data-testid={`pattern-review-${pattern.id}`}
        >
          Review <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function DataHintCard({ hint }) {
  if (!hint?.suggestions?.length) return null;
  return (
    <div
      className="rounded-2xl border border-border/70 bg-muted/40 px-4 py-3.5 flex items-start gap-3"
      data-testid="patterns-data-hint"
    >
      <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70 mb-1.5">
          How to get more from this page
        </p>
        <ul className="space-y-1.5">
          {hint.suggestions.map((s, i) => (
            <li key={i} className="text-sm text-muted-foreground leading-relaxed">{s}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * v3.4.8 Phase 2 — LLM big-picture narrative. Renders the cached
 * `narrative_summary` from /api/patterns/insights. A "Refresh insight"
 * chip force-regenerates via POST /api/patterns/insights/refresh-narrative
 * — used sparingly so we don't rack up token cost.
 */
function NarrativeCard({ summary, session, onRefreshed }) {
  const [refreshing, setRefreshing] = useState(false);
  if (!summary?.narrative) return null;

  const handleRefresh = async () => {
    if (!session?.access_token || refreshing) return;
    setRefreshing(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/patterns/insights/refresh-narrative`,
        {},
        { headers: { Authorization: `Bearer ${session.access_token}` } },
      );
      if (res.data?.narrative_summary) onRefreshed?.(res.data.narrative_summary);
    } catch (_e) {
      // Silent — the cached narrative already on screen stays visible.
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/[0.06] p-5"
      data-testid="patterns-narrative-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[hsl(var(--gold))]" />
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
            What Your Money Is Saying
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
          title="Regenerate this narrative"
          data-testid="patterns-narrative-refresh"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <div
        className="text-sm text-foreground leading-relaxed whitespace-pre-line"
        data-testid="patterns-narrative-text"
      >
        {summary.narrative}
      </div>
    </div>
  );
}

function LongTermLockedCard({ eligibility, baseCurrency }) {
  const remaining = eligibility?.unlocks_long_term_in_months ?? 13;
  return (
    <div
      className="rounded-2xl border border-dashed border-border/70 bg-muted/30 p-5"
      data-testid="patterns-long-term-locked"
    >
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 shrink-0 rounded-xl grid place-items-center bg-[hsl(var(--gold))]/10 text-[hsl(var(--gold))]">
          <Lock className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold">Long-term patterns</h4>
            <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/70 bg-muted rounded-full px-2 py-0.5">
              Coming soon
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
            Year-over-year comparisons, inflation-adjusted trends, and how
            your cost of living is evolving. Unlocks in{' '}
            <span className="font-semibold text-foreground">
              {remaining} more month{remaining === 1 ? '' : 's'}
            </span>{' '}
            of history — or import older statements to activate it now.
          </p>
          <p className="text-xs text-muted-foreground/80 mt-2 italic">
            Example: “Groceries in {baseCurrency} are up 14% year-on-year — outpacing local inflation of ~2%.
            Your income covered the gap, but at the cost of 3% less savings this year.”
          </p>
        </div>
      </div>
    </div>
  );
}

function GoalsLensCard({ goalsLens, navigate }) {
  if (!goalsLens?.has_goals) {
    return (
      <div
        className="rounded-2xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/[0.06] p-5"
        data-testid="patterns-goals-empty"
      >
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 shrink-0 rounded-xl grid place-items-center bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))]">
            <Target className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold">Turn patterns into progress.</h4>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
              Set a goal (emergency fund, down payment, holiday) and we&rsquo;ll
              show you how your current spend patterns are helping — or delaying — it.
            </p>
            <Button
              className="rounded-xl mt-3"
              onClick={() => navigate('/plan-ahead')}
              data-testid="patterns-goals-cta"
            >
              Set your first goal
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="patterns-goals-lens">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
        Goals lens
      </p>
      {goalsLens.goals.map((g) => (
        <div
          key={g.id}
          className="rounded-2xl border border-border/70 bg-card p-4"
          data-testid={`patterns-goal-${g.id}`}
        >
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 shrink-0 rounded-lg grid place-items-center bg-primary/10 text-primary">
              <Target className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">{g.name}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {g.current_amount > 0 ? `${Math.round((g.current_amount / g.target_amount) * 100)}% funded` : 'Not started'} · at current pace,{' '}
                {g.months_at_pace != null
                  ? `~${g.months_at_pace} month${g.months_at_pace === 1 ? '' : 's'} to go`
                  : 'set a monthly contribution to see ETA'}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PatternsTabV2({ baseCurrency = 'INR' }) {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.access_token) return () => {};
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/patterns/insights`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!cancelled) setData(res.data);
      } catch (_e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session?.access_token]);

  const onReview = (pattern) => {
    const p = new URLSearchParams({
      category: pattern.category,
      month: pattern.review_query?.month || '',
      type: 'expense',
    });
    navigate(`/transactions?${p.toString()}`);
  };

  const shortTermList = useMemo(() => data?.short_term_patterns || [], [data]);
  const featuredPattern = useMemo(() => {
    if (!data?.featured_pattern_id) return null;
    return shortTermList.find((p) => p.id === data.featured_pattern_id) || null;
  }, [data, shortTermList]);
  const otherPatterns = useMemo(
    () => shortTermList.filter((p) => p.id !== data?.featured_pattern_id),
    [shortTermList, data],
  );

  if (loading) {
    return (
      <div className="space-y-4" data-testid="insights-tab-patterns">
        <div className="rounded-2xl border border-border/70 bg-muted/30 h-24 animate-pulse" />
        <div className="rounded-2xl border border-border/70 bg-muted/30 h-32 animate-pulse" />
      </div>
    );
  }

  const currency = data?.base_currency || baseCurrency;

  return (
    <div className="space-y-5" data-testid="insights-tab-patterns">
      {/* Big-picture LLM narrative — cache-first, no re-write on refresh */}
      <NarrativeCard
        summary={data?.narrative_summary}
        session={session}
        onRefreshed={(next) => setData((d) => ({ ...(d || {}), narrative_summary: next }))}
      />

      {/* v3.5.0 — Quick-summary rail: 4 tiles surfacing the top signals
          so users can scan without reading paragraphs. */}
      <QuickSummaryRail quick={data?.quick_summary} />

      {/* v3.5.0 — Featured card: the single biggest change gets its own
          large surface. Everything else flows below in a compact list. */}
      <FeaturedPatternCard pattern={featuredPattern} baseCurrency={currency} onReview={onReview} />

      {/* Data hint / how-to-get-more */}
      <DataHintCard hint={data?.data_completeness_hint} />

      {/* Short-term patterns — the featured one is already rendered above,
          so here we show only the "other movers" as a compact list. */}
      {otherPatterns.length > 0 && (
        <div className="space-y-3" data-testid="patterns-short-term-section">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--gold))]" />
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
              Other movers · month-over-month
            </p>
          </div>
          {otherPatterns.map((p) => (
            <PatternRow key={p.id} pattern={p} baseCurrency={currency} onReview={onReview} />
          ))}
        </div>
      )}
      {shortTermList.length === 0 && data?.eligibility?.has_short_term && (
        <div
          className="rounded-2xl border border-border/70 bg-card p-5"
          data-testid="patterns-short-term-empty"
        >
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 shrink-0 rounded-xl grid place-items-center bg-emerald-100 text-emerald-600">
              <Minus className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Steady month.</p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                No significant category-level movement worth flagging vs
                last month. Your spending is running consistent.
              </p>
            </div>
          </div>
        </div>
      )}
      {!data?.eligibility?.has_short_term && (
        <div
          className="rounded-2xl border border-dashed border-border/70 bg-muted/30 p-5"
          data-testid="patterns-short-term-locked"
        >
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 shrink-0 rounded-xl grid place-items-center bg-muted text-muted-foreground">
              <Upload className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Add one more month of history.</p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                We need at least 2 months of transactions to compare and
                surface month-over-month spending patterns.
              </p>
              <Button
                variant="secondary" className="rounded-xl mt-3"
                onClick={() => navigate('/import')}
                data-testid="patterns-short-term-import-cta"
              >
                Import a statement
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Goals lens */}
      <GoalsLensCard goalsLens={data?.goals_lens} navigate={navigate} />

      {/* Long-term (YoY) — locked in Phase 1 */}
      <LongTermLockedCard eligibility={data?.eligibility} baseCurrency={currency} />
    </div>
  );
}

